import {
	BOOK_EXTERNAL_PAYMENT_STATUS,
	BOOK_STATUS,
	EXTERNAL_PAYMENT_STATUS,
	PASSENGER_TYPE,
	PAYMENT_COUPON_TYPE,
	PAYMENT_METHODS,
} from "app/constants";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import Cookies from "js-cookie";
import { buildPaymentTag } from "app/utils/analytics";

export const isExternalPaymentError = bookStatus => {
	return (
		bookStatus === BOOK_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR ||
		bookStatus === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR ||
		bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_TECHNICAL_ERROR ||
		bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_REFUSED ||
		bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_UNKNOWN
	);
};
export const isPaymentError = bookStatus => {
	return (
		bookStatus === BOOK_STATUS.CREDIT_CARD_TECHNICAL_ERROR ||
		bookStatus === BOOK_STATUS.CREDIT_CARD_3DS_REFUSED ||
		bookStatus === BOOK_STATUS.CREDIT_CARD_REFUSED ||
		bookStatus === BOOK_STATUS.CREDIT_CARD_INFO ||
		bookStatus === BOOK_STATUS.PAYMENT_AGREEMENT_REFUSED
	);
};
export const getPaymentErrorMessage = (response, phoneNumber) => {
	let errorMessage = {
		...{ id: "error.payment.error.unknown" },
		values: { telephone: phoneNumber },
	};

	const paymentType = get(response, "data.paymentType");
	const errorStatus = get(response, "data.status");

	if (errorStatus === BOOK_STATUS.CHILDREN_AGES_DISCREPANCY) {
		errorMessage = { id: "error.booking.children.age.discrepancy" };
	} else if (errorStatus === BOOK_STATUS.INFANTS_AGES_DISCREPANCY) {
		errorMessage = { id: "error.booking.infants.age.discrepancy" };
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_INFO) {
		errorMessage = { id: "error.payment.credit.card.info" };
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_REFUSED) {
		if (
			paymentType &&
			paymentType !== PAYMENT_METHODS.ADYEN_FLOW &&
			paymentType !== PAYMENT_METHODS.CARD
		) {
			const messageData = {
				...{ id: "error.payment.partner.refused" },
				values: { telephone: phoneNumber },
			};
			errorMessage = messageData;
		} else {
			errorMessage = { id: "error.payment.credit.card.refused.not.enough.balance" };
		}
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_3DS_REFUSED) {
		errorMessage = { id: "error.payment.credit.card.3ds.refused" };
	} else if (errorStatus === BOOK_STATUS.CREDIT_CARD_TECHNICAL_ERROR) {
		errorMessage = { id: "error.payment.technical.error" };
	} else if (errorStatus === BOOK_STATUS.WARNING_FULL_PROPERTY) {
		errorMessage = { id: "error.payment.full.property" };
	} else if (errorStatus === BOOK_STATUS.SESSION_EXPIRED) {
		errorMessage = { id: "error.payment.session.expired" };
	} else if (errorStatus === BOOK_STATUS.PAYMENT_AGREEMENT_REFUSED) {
		errorMessage = { id: "error.payment.consumer.credit.refused.error" };
	} else if (errorStatus === BOOK_STATUS.ERROR_MOBILE_PHONE_NUMBER_REQUIRED) {
		errorMessage = { id: "error.payment.floa.mobile.phone.required.error" };
	} else if (errorStatus === BOOK_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR) {
		const messageData = {
			...{ id: "error.payment.external" },
			values: { telephone: phoneNumber },
		};
		errorMessage = messageData;
	}
	return errorMessage;
};

/**
 * on recolte les data du navigateur pour le payload de /book (3ds2)
 */
export const buildBrowserInfos = () => {
	const screen = get(window, "screen");
	const navigator = get(window, "navigator");

	const screenWidth = screen ? screen.width : "";
	const screenHeight = screen ? screen.height : "";
	const colorDepth = screen ? screen.colorDepth : "";
	const javaEnabled = navigator ? navigator.javaEnabled() : false;

	const today = new Date();
	const timeZoneOffset = today.getTimezoneOffset();

	let language = "";
	if (window && window.navigator) {
		language = window.navigator.language
			? window.navigator.language
			: window.navigator.browserLanguage; // Else is for IE <+ 10
	}

	return {
		colorDepth,
		screenWidth,
		screenHeight,
		timeZoneOffset,
		javaEnabled,
		language,
	};
};

export const buildPaymentPayload = (
	values,
	{
		strategy = "",
		quotationCode = "",
		bookingFlowId = "",
		partnerData = {},
		merchandisingCode = "",
		marketingSource = "",
		booking = {},
		payment = {},
		promotion = {},
		baseUrl = "",
		isHotelOnly,
		isMobile,
		adyenFlowStateData,
	}
) => {
	const name = values?.contact?.name;
	const address = values?.contact?.address;
	const insurance = values?.insurance;
	const swissBillingFields = values?.payment?.swissBilling;
	const paymentMode = values?.payment?.mode;
	const paymentType = values?.payment?.type;
	const dateOfBirthRequired = payment?.dateOfBirthRequired;

	const passengers = values.passengers.map(passenger => {
		const passengerData = {
			type: passenger?.type,
			civility: passenger?.name?.gender,
			firstName: passenger?.name?.firstName,
			lastName: passenger?.name?.lastName,
			frequentFlyerTravellerCardNumber: passenger.frequentFlyerTravellerCardNumber,
		};

		// dateOfBirthRequired = true means that adults birthdates have to be sent along with children and infants birthdates
		// children and infants birthdates have to be sent in all cases
		if (
			(dateOfBirthRequired && passenger.type === PASSENGER_TYPE.ADULT) ||
			passenger.type === PASSENGER_TYPE.CHILD ||
			passenger.type === PASSENGER_TYPE.INFANT
		) {
			passengerData.dateOfBirth = {
				day: Number(passenger.birthdate?.day),
				year: Number(passenger.birthdate?.year),
				month: Number(passenger.birthdate?.month) + 1,
			};
		}
		return passengerData;
	});

	const payload = {
		strategy,
		code: quotationCode,
		bookingFlowId: quotationCode, // stopover only
		stopOverDuration: booking?.stopoverDuration, // stopover only
		customer: {
			civility: name.gender,
			firstName: name.firstName,
			lastName: name.lastName,
			email: values.contact.email,
			phone: values.contact.phone,
			address: {
				address1: address.line1,
				postCode: address.postCode,
				city: address.city,
				country: address.country,
				state: address.state,
			},
		},
		passengers: passengers,
		paymentType: paymentType, // TODO dupliqué avec payment.type. A supprimer une fois que toutes les brands sont sur le nouvelle api payement
		insurance: insurance,
		baseUrl,
		payment: {
			methodParams: {},
			terms: parseInt(paymentMode, 10),
			type: paymentType,
		},
		browserInfos: buildBrowserInfos(),
	};

	if (isHotelOnly) {
		payload.bookingFlowId = bookingFlowId;
		delete payload.strategy;
		delete payload.code;
		delete payload.passengers;
	}

	// Ajout du code merch pour le reporting des brands
	if (merchandisingCode) {
		partnerData.merchCode = merchandisingCode;
	}

	if (marketingSource) {
		partnerData.source = marketingSource;
	}

	if (!isEmpty(partnerData)) {
		payload.partnerData = partnerData;
	}

	const userAgent = navigator?.userAgent;

	payload.deviceType = isMobile ? "mobile" : "desktop";
	payload.userAgent = userAgent;

	if (paymentType === PAYMENT_METHODS.CARD) {
		const encryptedCard = payment?.encryptedCard;

		payload.payment.methodParams = {
			encryptedCard: encryptedCard,
		};
	} else if (paymentType === PAYMENT_METHODS.ADYEN_FLOW) {
		if (adyenFlowStateData) {
			payload.payment.methodParams.stateData = JSON.stringify(adyenFlowStateData);
		}
	} else if (paymentType === PAYMENT_METHODS.BCMC) {
		const encryptedCard = payment?.encryptedCard;

		if (encryptedCard) {
			payload.payment.methodParams = {
				encryptedCard: encryptedCard,
			};
		}

		if (adyenFlowStateData) {
			payload.payment.methodParams.stateData = JSON.stringify(adyenFlowStateData);
		}
	}

	if (paymentType === PAYMENT_METHODS.SWISS_BILLING) {
		payload.customer.dateOfBirth = {
			day: Number(swissBillingFields.birthdate.day),
			year: Number(swissBillingFields.birthdate.year),
			month: Number(swissBillingFields.birthdate.month) + 1,
		};
		payload.payment.methodParams.invoiceType = swissBillingFields.invoiceType;
	} else if (paymentType === PAYMENT_METHODS.IDEAL) {
		payload.payment.methodParams.issuerId = values.payment.issuerId;
	}

	if (booking.coupon === PAYMENT_COUPON_TYPE.PROMOCODE && promotion.promotionValue > 0) {
		payload.promoCode = promotion.promotionCode;
	} else if (booking.coupon === PAYMENT_COUPON_TYPE.PROMOCODE && !promotion.promotionValue) {
		payload.promoCode = "";
	}

	const insuranceData = payment?.insurances?.insurancesItem?.find(
		insurance => insurance.code === values.insurance
	);

	const paymentData = {
		insuranceName: insuranceData && insuranceData.name,
		paymentMode,
		paymentType,
	};

	const dataLayer = buildPaymentTag(paymentData);

	dataLayer["._ga"] = Cookies.get("_ga");

	payload.dataLayer = JSON.stringify(dataLayer);

	return payload;
};

export const getPaymentCardValues = selectedPaymentType => {
	if (
		selectedPaymentType === PAYMENT_METHODS.CARD ||
		selectedPaymentType === PAYMENT_METHODS.ADYEN_FLOW
	) {
		return {
			card: {
				amount: "",
				holderName: "",
				expiryMonth: "",
				expiryYear: "",
				cvc: "",
				number: "",
			},
		};
	} else if (selectedPaymentType === PAYMENT_METHODS.BCMC) {
		return {
			cardBCMC: {
				holderName: "",
				expiryMonth: "",
				expiryYear: "",
				number: "",
			},
		};
	} else if (selectedPaymentType === PAYMENT_METHODS.IDEAL) {
		return { issuerId: "" };
	} else if (selectedPaymentType === PAYMENT_METHODS.SWISS_BILLING) {
		return {
			swissBilling: {
				birthdate: {
					day: "",
					month: "",
					year: "",
				},
				invoiceType: "",
			},
		};
	}
	return null;
};

export const transformBookingContact = initialBookingContact => {
	if (
		!isEmpty(initialBookingContact) &&
		(!Object.prototype.hasOwnProperty.call(initialBookingContact, "name") ||
			!Object.prototype.hasOwnProperty.call(initialBookingContact, "address"))
	) {
		return {
			name: {
				gender: initialBookingContact["honorific-prefix"],
				lastName: initialBookingContact["family-name"],
				firstName: initialBookingContact["given-name"],
			},
			address: {
				line1: initialBookingContact["address-line1"],
				line2: initialBookingContact["address-line2"],
				postCode: initialBookingContact["postal-code"],
				city: initialBookingContact["address-level2"],
				country: initialBookingContact["country-name"],
				state: initialBookingContact.state,
			},
			phone: initialBookingContact.phone,
			email: initialBookingContact.email,
		};
	}
	return initialBookingContact;
};

export const getPaymentFieldNames = (fields, parentKey = "") => {
	if (!isEmpty(fields)) {
		let fieldNames = [];
		for (const key in fields) {
			if (typeof fields[key] === "object" && fields[key] !== null) {
				// Si la valeur est un objet, on appelle récursivement la fonction
				fieldNames = [
					...fieldNames,
					...getPaymentFieldNames(fields[key], `${parentKey}${key}.`),
				];
			} else {
				// Sinon, on ajoute le nom du champ à la liste
				fieldNames.push(`${parentKey}${key}`);
			}
		}
		return fieldNames;
	}
	return fields;
};

export const setNestedFieldErrors = (fieldPath, errorObject, setFieldError) => {
	Object.keys(errorObject).forEach(key => {
		const newFieldPath = `${fieldPath}.${key}`;
		if (Object.prototype.hasOwnProperty.call(errorObject[key], "id")) {
			// Si l'objet contient une propriété 'id', définir une erreur vide pour le champ
			setFieldError(newFieldPath);
		} else if (typeof errorObject[key] === "object" && errorObject[key] !== null) {
			// Si la valeur est un objet, appeler récursivement la fonction
			setNestedFieldErrors(newFieldPath, errorObject[key], setFieldError);
		}
	});
};
